import { Select, Typography } from '@/ui-kit/';
import styles from './direction-info-registration-form.module.scss';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { debounce, isArray } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { type Profile } from '@/types/profile';
import { ButtonSubmitStep } from '../button-submit-step';
import { callYaMetrika } from '@/utils/call-ya-metrika';
import { useToast } from '@/store/useToast';
import { SuccessNotificationIcon } from '@/assets/icons';
import { ANY_REGION_OPTION } from '@/utils/regions';
import { Region, SearchParamsRegions } from '@/api/region/types';
import { renderCity } from '@/utils/render-city';
import { SelectOption } from '@/ui-kit/select';
import { useGetRegions } from '@/hooks/use-get-regions';
import {
    RegistrationStepSevenSchema,
    registrationStepSevenSchema,
} from '@/schemes/registrationV2/registration-step-seven-schema';
import { eventsApi } from '@/api';
import { useAuth } from '@/store/useAuth';
import { useSuccessfulMessage } from '@/store/useSuccessfulMessage';
import WebApp from '@twa-dev/sdk';
import { useVibration } from '@/store/useVibration';

const DELAY_SEND_DATA = 1000;

interface UpdateUserData extends RegistrationStepSevenSchema {
    state?: Profile['state'];
}

interface Props {
    userData?: Profile | null;
    onUpdateUserData: (data: UpdateUserData) => Promise<any>;
    nextStep: () => void;
}

export const DirectionInfoRegistrationForm: React.FC<Props> = ({ userData, onUpdateUserData, nextStep }) => {
    const showToast = useToast((state) => state.show);
    useEffect(() => {
        callYaMetrika('new_search_direction_screen');
        eventsApi.updateUserEvent('new_search_direction_screen');
        eventsApi.updateUserEvent('registration_step_view_search_direction_screen')
    }, []);

    const [searchParamsCities, setSearchParamsCities] = useState<SearchParamsRegions>({ q: '' });
    const { isLoading: isLoadingCities, data: dataRegions } = useGetRegions(searchParamsCities);
    const [search, setSearch] = useState<string>('');
    const [isDisabled, setDisabled] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRegions, setSelectedRegions] = useState<any[]>([]);

    const { setIsOpen } = useSuccessfulMessage((state) => state);

    const initialValues = useMemo(() => {
        console.log('userData?.regions ', userData?.regions);
        return {
            regions: userData?.regions ?? [],
        };
    }, [userData]);

    const defaultValues = useMemo(() => {
        const regions = userData?.regions
            ? userData?.regions.length
                ? userData?.regions.map((region) => ({
                      label: renderCity(region),
                      value: region.id,
                  }))
                : [ANY_REGION_OPTION]
            : [ANY_REGION_OPTION];

        // Устанавливаем начальные выбранные регионы
        setSelectedRegions(regions);

        return { regions };
    }, [userData]);

    const methods = useForm<RegistrationStepSevenSchema>({
        resolver: zodResolver(registrationStepSevenSchema),
        defaultValues: defaultValues,
    });

    const {
        handleSubmit,
        formState: { errors, isDirty, isValid },
        setValue,
        getValues,
    } = methods;

    const handleChangeUserDataDebounce = useCallback(
        debounce(async (data: any) => {
            console.log('handleChangeUserDataDebounce', data);
            await onUpdateUserData(data);
        }, DELAY_SEND_DATA),
        [onUpdateUserData],
    );

    // Функция для отправки данных на бэкенд
    const sendRegionsToBackend = useCallback((regions: any[]) => {
        console.log('regions', regions);
        handleChangeUserDataDebounce({
            regions: regions,
            region_ids: regions.map((item: { value: any }) => item.value)
        });
    }, []);

    const completeRegistration = useAuth(state => state.completeRegistration)

    const onSubmit = async (data: RegistrationStepSevenSchema) => {
        try {
            setIsLoading(true);
            await onUpdateUserData({ state: 'complete', ...data });
            await completeRegistration()
            await new Promise<void>((resolve) => {
                setTimeout(() => {
                    nextStep();
                    eventsApi.updateUserEvent('registration_step_complete_search_direction_screen')
                    setIsOpen(true);
                    resolve();
                }, 500);
            });
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearchCityDebounce = useCallback(
        (e: string): void => {
            setSearch(e);
            debounce((q: string) => {
                setSearchParamsCities({ q: q.trim() });
            }, DELAY_SEND_DATA)(e);
        },
        [searchParamsCities],
    );

    const handleRegionChange = useCallback(
        (val: any, setState: any) => {
            console.log('onSelected', val);

            if (isArray(val)) {
                let newRegions = val;

                if (val.length) {
                    if (val[val.length - 1].value === -1) {
                        newRegions = [ANY_REGION_OPTION];
                        setState([ANY_REGION_OPTION]);
                    } else if (val.map((e: SelectOption) => e.value).includes(ANY_REGION_OPTION.value)) {
                        newRegions = val.filter((e: SelectOption) => e.value !== ANY_REGION_OPTION.value);
                        setState(newRegions);
                    } else {
                        setState(val);
                    }
                }

                // Обновляем значение в форме
                setValue('regions', newRegions, { shouldValidate: true });

                // Обновляем локальное состояние
                setSelectedRegions(newRegions);

                // Отправляем данные на бэкенд
                sendRegionsToBackend(newRegions);

                // Проверяем, изменились ли регионы
                const regionsIds = initialValues.regions?.map((e: Region) => e.id).sort();
                const newRegionsIds = newRegions.map((e: Record<string, any>) => e.value).sort();

                const isInitialRegionsWhenAnyRegionWasNotSelected =
                    newRegionsIds.every((e) => regionsIds?.includes(e)) && newRegionsIds.length === regionsIds?.length;
                const isInitialRegionsWhenAnyRegionWasSelected =
                    regionsIds?.length === 0 && newRegionsIds.includes(ANY_REGION_OPTION.value);
                const isInitialRegions =
                    isInitialRegionsWhenAnyRegionWasNotSelected || isInitialRegionsWhenAnyRegionWasSelected;

                setDisabled(newRegions.length === 0 || isInitialRegions);
            }
        },
        [initialValues.regions, setValue, sendRegionsToBackend],
    );

    const { isVibrationEnabled } =
        useVibration(state => state);

    return (
        <FormProvider {...methods}>
            <form
                className={styles.form}
                onSubmit={handleSubmit(onSubmit)}
            >
                <div className={styles.formContainer}>
                    <div className={styles.formContainerHeader}>
                        <Typography
                            variant="p"
                            fontWeight="medium"
                            className={styles.titleForm}
                            center
                        >
                            Укажи направление поиска партнера
                        </Typography>

                        <p className={styles.formInfoText}>
                            Выбери город или страну, чтобы видеть анкеты других людей. Ты можешь изменить это, когда
                            захочешь
                        </p>
                    </div>

                    <div className={styles.formContainerTarget}>
                        <Select
                            name="regions"
                            placeholder="Выберите город"
                            isLoading={isLoadingCities}
                            wrapperClassName={styles.selectWrap}
                            options={[
                                ...(search.length > 0 ? [] : [ANY_REGION_OPTION]),
                                ...(search.length > 0
                                    ? (dataRegions?.data?.map((region: Region) => ({
                                          label: renderCity(region),
                                          value: region.id,
                                      })) ?? [])
                                    : []),
                            ]}
                            isMulti={true}
                            isClearable={false}
                            onSearch={handleSearchCityDebounce}
                            onSelected={handleRegionChange}
                        />
                    </div>
                </div>

                <ButtonSubmitStep
                    isLoading={isLoading}
                    title="Завершить регистрацию"
                    stepNumber={7}
                    isDisabled={!isValid}
                    onClick={() => {
                        if (isVibrationEnabled) {
                            WebApp
                                .HapticFeedback
                                .impactOccurred('medium');
                            // pass for not android
                            navigator.vibrate?.(10)
                        }
                    }} />
            </form>
        </FormProvider>
    );
};
